import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from 'react';

const ChildModal = ({id, onClose,}: {id?: string; onClose: () => void;}) => {
	const API_URL = import.meta.env.VITE_API_URL;
	const queryClient = useQueryClient();
	const [child, setChild] = useState({name: "", birth: "", gender: ""});
	const [childError, setChildError] = useState({name: "", birth: "", gender: ""});

	const { data : childData } = useQuery({
		queryKey: ['child', id],
		queryFn: async () => {
			if (!id) return null;
			
			const params = new URLSearchParams();
			params.append("ch_id", id);

			const response = await fetch(`${API_URL}/bbs/ajax.get_child.php`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: params,
				credentials: "include",
			});

			if (!response.ok) {
				throw new Error("네트워크 오류");
			}

			return response.json();
		},
		enabled: !!id,
	});
	const childItem = childData?.data ?? null;

	const mutation = useMutation({
		mutationFn: async (childData: {name: string; birth: string; gender: string;}) => {
			const params = new URLSearchParams();
			if (id) params.append("ch_id", id);
			params.append("name", childData.name);
			params.append("birth", childData.birth);
			params.append("gender", childData.gender);

			const response = await fetch(`${API_URL}/bbs/ajax.update_child.php`, {
					method: "POST",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: params,
					credentials: "include",
				}
			);

			if (!response.ok) {
				throw new Error("네트워크 오류");
			}

			return response.json();
		},
		onSuccess: (response) => {
			if (!response.success) {
				switch (response.code) {
					case "INVALID_REQUEST":
						alert("필수 필드값이 입력되지 않았습니다.");
						break;
					default:
						alert("알 수 없는 오류가 발생했습니다.");
						break;
				}
				return;
			}

			if(id) alert("자녀 정보가 수정되었습니다.");
			else alert("자녀가 등록되었습니다.");

			queryClient.invalidateQueries({queryKey: ['member']});
			if (id) queryClient.invalidateQueries({queryKey: ['child', id]});
			if (id) queryClient.invalidateQueries({queryKey: ['passportList']});
			onClose();
		},
		onError: (error: any) => {
			console.log(error.message);
		},
	});

	const handleChildValidate = (field: string, value: string) => {
		let validValue = value;
		let errorMsg = "";

		if (field === "name") {
			validValue = value.replace(/[^a-zA-Z가-힣ㄱ-ㅎㅏ-ㅣ]/g, "");

			if (!validValue.trim()) {
				errorMsg = "이름을 입력해주세요.";
			}
		}

		if (field === "birth") {
			let numbers = value.replace(/[^0-9]/g, "").slice(0, 8);
			if (numbers.length < 5) {
				validValue = numbers;
			} else if (numbers.length < 7) {
				validValue = `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
			} else {
				validValue = `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6)}`;
			}

			if(!numbers) {
				errorMsg = "생년월일을 입력해주세요.";
			} else if (numbers.length === 8) {
				const year = Number(numbers.slice(0, 4));
				const month = Number(numbers.slice(4, 6));
				const day = Number(numbers.slice(6, 8));

				if (month < 1 || month > 12 || day < 1 || day > 31) {
					errorMsg = "올바른 생년월일을 입력해주세요.";
				}
			} else {
        		errorMsg = "올바른 생년월일을 입력해주세요.";
    		}
		}

	    if (field === "gender") {
	        validValue = value;
	        if (!validValue) errorMsg = "성별을 선택해주세요.";
	    }

		return {
			value: validValue,
			error: errorMsg,
		};
	};

	const handleChildComplete = () => {
		const nameValidation = handleChildValidate("name", child.name);
		const birthValidation = handleChildValidate("birth", child.birth);
		const genderValidation = handleChildValidate("gender", child.gender);
		setChildError({name: nameValidation.error, birth: birthValidation.error, gender: genderValidation.error});
		if (nameValidation.error || birthValidation.error || genderValidation.error) return;

		if (id && !confirm("정말로 변경하시겠습니까?")) return;

		mutation.mutate(child);
	};

	const handleChildChange = (field: string, value: string) => {
		const validated = handleChildValidate(field, value);

		setChild((prev) => ({
			...prev,
			[field]: validated.value,
		}));

		setChildError((prev) => ({
			...prev,
			[field]: validated.error,
		}));
	};

	useEffect(() => {
		if (childItem) {
			setChild({
				name: childItem.name ?? "",
				birth: childItem.birth ?? "",
				gender: childItem.gender ?? "",
			});
		}
	}, [childItem]);
	return (
		<div className="fixed inset-0 bg-black/70 z-[1000] flex items-center justify-center p-5 ">
			<div className="p-5 border rounded-xl bg-white space-y-2 shadow-sm min-w-[300px] max-w-[400px]">
				<label  className="block text-sm font-semibold text-gray-700 mb-1">
					이름
				</label>
				<input
					type="text"
					placeholder="자녀 이름을 입력해주세요."
					value={child?.name}
					onChange={(e) => handleChildChange('name', e.target.value)}
					className={`
						w-full px-4 py-3 rounded-lg border text-sm
						${childError.name
							? "border-red-500 focus:border-red-500"
							: "border-gray-300 focus:border-blue-500"
						}
					`}
				/>
				{childError.name && (
					<p className="text-sm text-red-500 mt-1">
						{childError.name}
					</p>
				)}
				<label  className="block text-sm font-semibold text-gray-700 mb-1">
					생년월일
				</label>
				<input
					type="text"
					placeholder="생년월일(8자리)을 입력해주세요."
					value={child?.birth}
					onChange={(e) => handleChildChange('birth', e.target.value)}
					className={`
						w-full px-4 py-3 rounded-lg border text-sm
						${childError.birth
							? "border-red-500 focus:border-red-500"
							: "border-gray-300 focus:border-blue-500"
						}
					`}
				/>
				{childError.birth && (
					<p className="text-sm text-red-500 mt-1">
						{childError.birth}
					</p>
				)}
				<label  className="block text-sm font-semibold text-gray-700 mb-1">
					성별
				</label>
				<div className="flex w-full rounded-xl border overflow-hidden">
					
					<label className={`flex-1 text-center py-3 cursor-pointer transition text-sm ${child?.gender === "M" ? "bg-purple-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
						<input type="radio" name={`gender-1`} value="M" checked={child?.gender === "M"} onChange={(e) => handleChildChange('gender', e.target.value)} className="hidden" />
						남자
					</label>
					<label className={`flex-1 text-center py-3 cursor-pointer transition text-sm border-l ${child?.gender === "F" ? "bg-purple-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
						<input type="radio" name={`gender-2`} value="F" checked={child?.gender === "F"} onChange={(e) => handleChildChange('gender', e.target.value)} className="hidden" />
						여자
					</label>
				</div>
				{childError.gender && (
					<p className="text-sm text-red-500 mt-1">
						{childError.gender}
					</p>
				)}
				<div className="flex gap-2">
					<button
						type="button"
						onClick={onClose}
						className="w-full px-4 py-3 rounded-lg text-sm font-semibold transition whitespace-nowrap mt-2 bg-gray-200 transition-colors hover:bg-gray-300"
			        >
			            닫기
			        </button>
					<button
						type="button"
						onClick={handleChildComplete}
						disabled={!child?.name || !child?.birth || !child?.gender}
						className={`w-full px-4 py-3 rounded-lg text-sm font-semibold transition whitespace-nowrap mt-2
			                ${child?.name && child?.birth && child?.gender
			                	? "bg-purple-600 text-white hover:bg-purple-700"
			                	: "bg-gray-200 text-gray-400 cursor-not-allowed"}
			                	`}
			                >
			            입력 완료
			        </button>
				</div>
			</div>
		</div>
	);
}

export default ChildModal;